import { useEffect, useState } from 'react';

import {
  Box,
  Button,
  Center,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  Spinner,
  Text,
} from '@chakra-ui/react';

import { useQueryClient } from '@tanstack/react-query';

import {
  AuthenticationMethod,
  CheckOutline,
  DropdownInput,
  DropdownOption,
  useToast,
} from '@aslanpay/portal-common-lib';

import useFetchCompanyConfiguration from 'app/hooks/useFetchCompanyConfiguration';
import useUpdateCompanyConfiguration from 'app/hooks/useUpdateCompanyConfiguration';

import { DefaultAuthMethodSectionProps } from './DefaultAuthMethodSection.types';

const AUTH_METHOD_OPTIONS: DropdownOption[] = [
  { value: 'COGNITO_USERNAME_PASSWORD', text: 'Username & password (Cognito)' },
  { value: 'SSO_ENTRA', text: 'Microsoft Entra SSO' },
  { value: 'PARTNER_CLIENT_CREDENTIALS', text: 'Partner client credentials' },
];

function DefaultAuthMethodSection({ companyId = '' }: DefaultAuthMethodSectionProps) {
  const queryClient = useQueryClient();
  const toast = useToast();

  const { data: companyConfiguration, isInitialLoading } = useFetchCompanyConfiguration(companyId);

  const { mutateAsync: updateCompanyConfigurationAsync } = useUpdateCompanyConfiguration({
    onSuccess: () => {},
    onError: () => {},
  });

  const initialMethod = companyConfiguration?.defaultAuthenticationMethod;

  const [selectedMethod, setSelectedMethod] = useState<AuthenticationMethod | undefined>(undefined);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setSelectedMethod(initialMethod);
  }, [initialMethod]);

  const hasChanged = !!selectedMethod && selectedMethod !== initialMethod;

  const handleSave = async () => {
    if (!companyConfiguration || !selectedMethod) return;

    setIsSaving(true);
    try {
      await updateCompanyConfigurationAsync({
        id: companyConfiguration.companyId,
        defaultAuthenticationMethod: selectedMethod,
      });

      toast({
        position: 'top-right',
        status: 'success',
        title: 'Configuration saved',
        description: 'The default authentication method was successfully saved.',
      });

      queryClient.invalidateQueries({
        queryKey: ['fetchCompanyConfiguration', companyConfiguration.companyId],
      });
    } catch (error) {
      toast({
        position: 'top-right',
        status: 'error',
        title: 'Something went wrong',
        description: 'It was not possible to save the default authentication method.',
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (isInitialLoading) {
    return (
      <Center py="40px">
        <Spinner color="brand.purple" size="md" />
      </Center>
    );
  }

  const defaultOption = AUTH_METHOD_OPTIONS.find(
    (option) => option.value === (selectedMethod ?? initialMethod)
  );

  return (
    <Box border="1px solid" borderColor="brand.lightGray1" borderRadius="8px" p="20px">
      <Text as="h5" textStyle="h5SemiBold" mb="12px">
        Authentication
      </Text>
      <FormControl>
        <FormLabel>Default authentication method</FormLabel>
        <Flex align="center" gap="20px" wrap="wrap">
          <Box w="full" maxW="360px">
            <DropdownInput
              key={initialMethod ?? 'none'}
              placeholder="Select authentication method"
              options={AUTH_METHOD_OPTIONS}
              defaultValue={defaultOption}
              onChange={(value) => setSelectedMethod(value as AuthenticationMethod)}
            />
          </Box>
          <Button
            leftIcon={<CheckOutline boxSize="20px" />}
            size="sm"
            onClick={handleSave}
            isLoading={isSaving}
            isDisabled={!hasChanged || isSaving}
            data-testid="save-auth-method-button"
          >
            Save changes
          </Button>
        </Flex>
        <FormHelperText mt="8px">
          The default authentication method for this company&apos;s users.
        </FormHelperText>
      </FormControl>
    </Box>
  );
}

export { DefaultAuthMethodSection };
